import { useState } from "react";
import useWebTracking from "@/hooks/useWebTracking";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, ShieldCheck, Crown } from "lucide-react";
import api, { setSession, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function Login() {
    useWebTracking();
    const nav = useNavigate();
    const [form, setForm] = useState({ email: "", password: "", totp_code: "" });
    const [busy, setBusy] = useState(false);
    const [needTfa, setNeedTfa] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/auth/login", form);
            if (data.two_factor_required && !data.access_token) {
                setNeedTfa(true);
                toast.info("Enter your 6-digit TOTP code to continue.");
                return;
            }
            setSession(data.access_token, data.user);
            toast.success(`Welcome ${data.user.full_name}`);
            // Route each role to its home. Admins signing in via the generic
            // doctor portal are still bounced to the correct console — the
            // dedicated /admin/login and /super-admin/login pages are just a
            // cleaner front door, not the only way in.
            if (data.user.role === "doctor") nav("/doctor");
            else if (data.user.role === "super_admin") nav("/super-admin");
            else nav("/admin");
        } catch (err) {
            toast.error(errMsg(err, "Login failed"));
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[1.05fr_1fr]">
            <div className="hidden lg:flex ddi-auth-panel text-white p-12 flex-col justify-between relative">
                <Link to="/" className="inline-flex items-center gap-2.5 text-lg font-bold relative z-10" data-testid="login-brand-desktop">
                    <img src="/brand-icon.png" alt="Doctor Digital ID" className="h-10 w-10 object-contain" />
                    <span>DOCTOR <span className="text-[#60A5FA]">DIGITAL ID</span></span>
                </Link>
                <div className="max-w-md relative z-10">
                    <div className="ddi-eyebrow text-[#60A5FA] mb-3">Verified · Trusted · Connected</div>
                    <h1 className="text-4xl xl:text-5xl font-bold leading-[1.08] tracking-tight">
                        Welcome back.
                    </h1>
                    <p className="mt-5 text-white/75 text-[15px] leading-relaxed">
                        Sign in to continue building your digital identity, manage packages, and download invoices.
                    </p>
                    <div className="mt-8 grid grid-cols-2 gap-3 max-w-sm">
                        <div className="rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm p-3">
                            <div className="text-[11px] uppercase tracking-widest text-white/50 font-semibold">Grounded AI</div>
                            <div className="text-sm font-semibold text-white mt-1">Zero fabrication</div>
                        </div>
                        <div className="rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm p-3">
                            <div className="text-[11px] uppercase tracking-widest text-white/50 font-semibold">Approvals</div>
                            <div className="text-sm font-semibold text-white mt-1">Admin + Doctor</div>
                        </div>
                    </div>
                </div>
                <div className="text-xs text-white/50 relative z-10">
                    Test creds: doctor@platform.test · Doctor@123
                </div>
            </div>
            <div className="flex items-center justify-center bg-[#F5F7FB] p-6 md:p-12">
                <form onSubmit={submit} className="ddi-card ddi-shadow-md p-8 sm:p-9 w-full max-w-md" data-testid="login-form">
                    <Link to="/" className="flex items-center gap-2.5 w-fit lg:hidden mb-6" data-testid="login-brand-mobile">
                        <img src="/brand-icon.png" alt="Doctor Digital ID" className="h-9 w-9 object-contain" />
                        <span className="text-[14px] font-bold tracking-tight text-[#123B5D]">
                            DOCTOR <span className="text-[#2563EB]">DIGITAL ID</span>
                        </span>
                    </Link>
                    <div className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-[#0F766E]/10 text-[#0F766E]">
                        <LogIn size={18} />
                    </div>
                    <h2 className="mt-4 text-[26px] font-bold text-[#123B5D] tracking-tight">
                        Log in
                    </h2>
                    <p className="mt-1.5 text-[13.5px] text-[#64748B]">
                        Continue to your workspace.
                    </p>
                    <div className="mt-7 space-y-4">
                        <div>
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                value={form.email}
                                onChange={(e) =>
                                    setForm({ ...form, email: e.target.value })
                                }
                                data-testid="login-email"
                                required
                                autoComplete="email"
                            />
                        </div>
                        <div>
                            <Label htmlFor="password">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                value={form.password}
                                onChange={(e) =>
                                    setForm({ ...form, password: e.target.value })
                                }
                                data-testid="login-password"
                                required
                                autoComplete="current-password"
                            />
                        </div>
                        {needTfa && (
                            <div>
                                <Label htmlFor="totp">6-digit TOTP code</Label>
                                <Input
                                    id="totp"
                                    type="text"
                                    inputMode="numeric"
                                    value={form.totp_code}
                                    onChange={(e) => setForm({ ...form, totp_code: e.target.value })}
                                    data-testid="login-totp"
                                    placeholder="123456"
                                    required
                                    autoComplete="one-time-code"
                                />
                            </div>
                        )}
                    </div>
                    <button
                        type="submit"
                        className="mt-6 w-full ddi-teal-btn justify-center disabled:opacity-60"
                        disabled={busy}
                        data-testid="login-submit"
                    >
                        {busy ? "Signing in…" : "Sign in"}
                    </button>
                    <div className="mt-5 flex items-center gap-3 text-[10.5px] uppercase tracking-[0.24em] text-[#17212B]/40 font-semibold">
                        <div className="flex-1 h-px bg-[#EEF1F6]" />
                        <span>or</span>
                        <div className="flex-1 h-px bg-[#EEF1F6]" />
                    </div>
                    <div className="mt-4">
                        <GoogleSignInButton testId="login-google-btn" />
                    </div>
                    <div className="mt-5 text-sm flex items-center justify-between">
                        <Link to="/forgot-password" className="text-[#0F766E] font-semibold hover:underline" data-testid="login-forgot">
                            Forgot password?
                        </Link>
                        <Link to="/signup" className="text-[#17212B]/70 hover:text-[#123B5D] font-medium" data-testid="login-goto-signup">
                            Create account
                        </Link>
                    </div>
                    <div className="mt-6 pt-4 border-t border-[#EEF1F6] flex items-center justify-between text-[12px] text-[#17212B]/60">
                        <Link
                            to="/admin/login"
                            className="inline-flex items-center gap-1.5 hover:text-[#123B5D] transition-colors"
                            data-testid="login-goto-admin"
                        >
                            <ShieldCheck size={13} /> Admin sign-in
                        </Link>
                        <Link
                            to="/super-admin/login"
                            className="inline-flex items-center gap-1.5 hover:text-[#123B5D] transition-colors"
                            data-testid="login-goto-super-admin"
                        >
                            <Crown size={13} /> Super admin
                        </Link>
                    </div>
                </form>
            </div>
        </div>
    );
}
